import { connect } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
import { select, toggleTriggers, showMaximizedChart, hideMaximizedChart } from '../../actions';
import {
  get2dData,
  getInitialVol,
  getInflationCurveChartData,
  getinflationSensitivityChartData,
  getVolatilitySensitivityChartData,
  getPriceChartData,
  getKeyFiguresTableData,
  getThreshold
} from './selectors';
import React from 'react';
import { Modal } from 'react-bootstrap';
import * as d3 from 'd3';
import Alert from '../../components/Alert';
import InflationCurveChart from '../../charts/InflationCurveChart';
import SensitivityChart from '../../charts/SensitivityChart';
import PriceChart from '../../charts/PriceChart';
import KeyFiguresTable from '../../charts/KeyFiguresTable';

export const ASSET_INFLATION_SENSITIVITY = 'Asset Inflation Sensitivity';
export const LIABILITY_INFLATION_SENSITIVITY = 'Liability Inflation Sensitivity';
export const ASSET_PRICE = 'Asset Price';
export const LIABILITY_PRICE = 'Liability Price';
export const FUNDING_LEVEL_GAIN = 'Funding Level Gain';
export const INFLATION_HEDGE_EFFECTIVENESS = 'Inflation Hedge Effectiveness';
export const RATES_HEDGE_EFFECTIVENESS = 'Rates Hedge Effectiveness';

const layouts = {
  lg: [
    { i: 'inflation-chart', x: 0, y: 0, w: 4, h: 4, minW: 2, minH: 2 },
    { i: 'inflation-sensitivity-chart', x: 4, y: 0, w: 4, h: 4, minW: 2, minH: 2 },
    { i: 'volatility-sensitivity-chart', x: 8, y: 0, w: 4, h: 4, minW: 2, minH: 2 },
    { i: 'price-chart', x: 0, y: 4, w: 8, h: 4, minW: 4, minH: 2 },
    { i: 'key-figures-table', x: 8, y: 4, w: 4, h: 4, minW: 4, minH: 2 },
  ],
  xxs: [
    { i: 'inflation-chart', x: 0, y: 0, w: 1, h: 4, static: true },
    { i: 'inflation-sensitivity-chart', x: 0, y: 4, w: 1, h: 4, static: true },
    { i: 'volatility-sensitivity-chart', x: 0, y: 8, w: 1, h: 4, static: true },
    { i: 'price-chart', x: 0, y: 12, w: 1, h: 4, static: true },
    { i: 'key-figures-table', x: 0, y: 16, w: 1, h: 4, static: true },
  ],
};

const ReactResponsiveGridLayout = WidthProvider(Responsive);

const Dashboard = (props) => {
  if (props.isError) {
    return <Alert
      title="Unable to parse data"
      details={props.errorInfo.toString()}
      stackTrace={props.errorInfo.stack}
    />
  }

  const charts = {
    inflationChart: <InflationCurveChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('inflationChart')}
      data={props.inflationCurveChartData}
      onSelected={props.onSelected}
      onReset={() => { props.onSelected('infAdj', props.initialInfAdj); props.onSelected('vol', props.initialVol); }}
      vol={props.vol}
      infAdj={props.infAdj}
      title="Inflation Curve"
    />,
    inflationSensitivityChart: <SensitivityChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('inflationSensitivityChart')}
      data={props.inflationSensitivityChartData}
      onSelected={props.onSelected}
      onViewSelected={view => props.onSelected('currentView', view)}
      onReset={() => props.onSelected('infAdj', props.initialInfAdj)}
      onToggleTriggers={props.onToggleTriggers}
      title="Impact of Inflation Level"
      selected={{ key: 'infAdj', value: props.infAdj }}
      threshold={props.threshold}
      tolerance={props.tolerance}
      triggersEnabled={props.triggersEnabled}
      displayOptions={props.options}
      selectedDisplayOption={props.currentView}
      units={props.units}
      legend={props.legend}
      xLabel="Inflation Level Adjustment"
      yLabel={props.currentView}
      xAxisFormatter={d3.format('.0%')}
      yAxisFormatter={props.yAxisFormatter}
    />,
    volatilitySensitivityChart: <SensitivityChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('volatilitySensitivityChart')}
      data={props.volatilitySensitivityChartData}
      onSelected={props.onSelected}
      onViewSelected={view => props.onSelected('currentView', view)}
      onReset={() => props.onSelected('vol', props.initialVol)}
      onToggleTriggers={props.onToggleTriggers}
      title="Impact of Inflation Volatility"
      selected={{ key: 'vol', value: props.vol }}
      threshold={props.threshold}
      tolerance={props.tolerance}
      triggersEnabled={props.triggersEnabled}
      displayOptions={props.options}
      selectedDisplayOption={props.currentView}
      units={props.units}
      legend={props.legend}
      xLabel="Inflation Volatility"
      yLabel={props.currentView}
      xAxisFormatter={d3.format('.1%')}
      yAxisFormatter={props.yAxisFormatter}
    />,
    priceChart: <PriceChart
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('priceChart')}
      data={props.priceChartData}
      onViewSelected={view => props.onSelected('items', view)}
      title={props.items}
      selected={props.items}
      displayOptions={['Price', 'Delta', 'Sensitivity']}
      selectedDisplayOption={props.items}
    />,
    keyFiguresTable: <KeyFiguresTable
      isLoading={props.isLoading}
      onMaximized={() => props.onMaximized('keyFiguresTable')}
      title="Performance of Initial Hedge"
      data={props.keyFiguresTableData}
    />,
  }

  const modalChart = props.showMaximizedChart ? React.cloneElement(
    charts[props.maximizedChart],
    { onMaximized: null, onMinimized: () => props.onMinimized() },
  ) : null;

  return (
    <div>
      <ReactResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 800, xxs: 0 }}
        cols={{ lg: 12, xxs: 1 }}
        rowHeight={80}
        measureBeforeMount={true}
      >
        <div key="inflation-chart">
          {charts.inflationChart}
        </div>
        <div key="inflation-sensitivity-chart">
          {charts.inflationSensitivityChart}
        </div>
        <div key="volatility-sensitivity-chart">
          {charts.volatilitySensitivityChart}
        </div>
        <div key="price-chart">
          {charts.priceChart}
        </div>
        <div key="key-figures-table">
          {charts.keyFiguresTable}
        </div>
      </ReactResponsiveGridLayout>
      <div className="maximized-chart">
        <Modal show={props.showMaximizedChart} onHide={props.onMinimized} dialogClassName="maximized-chart-modal">
          {modalChart}
        </Modal>
      </div>
    </div>
  );
}

const mapStateToProps = (state, props) => {
  if (props.isLoading) {
    return {
      isLoading: true,
    };
  }

  const measures = [
    ASSET_INFLATION_SENSITIVITY,
    LIABILITY_INFLATION_SENSITIVITY,
    ASSET_PRICE,
    LIABILITY_PRICE,
    FUNDING_LEVEL_GAIN,
    INFLATION_HEDGE_EFFECTIVENESS,
    RATES_HEDGE_EFFECTIVENESS,
  ];

  const views = {
    'Inflation Sensitivity': [{ label: ASSET_INFLATION_SENSITIVITY, units: 'percentage', 'legend': 'Asset' }, { label: LIABILITY_INFLATION_SENSITIVITY, units: 'percentage', 'legend': 'Liability' }],
    'Price': [{ label: ASSET_PRICE, units: 'absolute', 'legend': 'Asset' }, { label: LIABILITY_PRICE, units: 'absolute', 'legend': 'Liability' }],
    'Funding Level Gain': [{ label: FUNDING_LEVEL_GAIN, units: 'ratio', 'legend': 'Gain' }],
    'Inflation Hedge Effectiveness': [{ label: INFLATION_HEDGE_EFFECTIVENESS, units: 'ratio', 'legend': 'Hedge Effectiveness' }],
    'Rates Hedge Effectiveness': [{ label: RATES_HEDGE_EFFECTIVENESS, units: 'ratio', 'legend': 'Hedge Effectiveness' }],
  };

  try {
    const inflationCurve = props.data.InflationCurve.data;
    const inflationSurface = props.data.InflationSurface.data;
    const ldiCharacteristics = props.data.LdiCharacteristics.data;

    const isDataPresent = state.local.investible_benchmark === undefined;

    const surfaces = {};
    measures.forEach(x => surfaces[x] = get2dData({ inflationSurface, property: x }));

    const initialInfAdj = 0;
    const initialVol = getInitialVol(inflationSurface);
    const triggersEnabled = state.local.triggersEnabled;

    const currentView = isDataPresent || state.local.investible_benchmark.currentView === undefined ? FUNDING_LEVEL_GAIN : state.local.investible_benchmark.currentView;
    const infAdj = isDataPresent || state.local.investible_benchmark.infAdj === undefined ? 0 : state.local.investible_benchmark.infAdj;
    const vol = isDataPresent || state.local.investible_benchmark.vol === undefined ? initialVol : state.local.investible_benchmark.vol;
    const threshold = isDataPresent || state.local.investible_benchmark.threshold === undefined ? (views[currentView].length === 1 ? getThreshold(inflationSurface, ldiCharacteristics, surfaces, currentView) : null) : state.local.investible_benchmark.threshold;
    const tolerance = isDataPresent || state.local.investible_benchmark.tolerance === undefined ? 0.1 : state.local.investible_benchmark.tolerance;
    const items = isDataPresent || state.local.investible_benchmark.items === undefined ? 'Price' : state.local.investible_benchmark.items;

    const inflationSensitivityChartData = getinflationSensitivityChartData(infAdj, vol, inflationSurface, ldiCharacteristics, surfaces, views[currentView].map(x => x.label));
    const inflationCurveChartData = getInflationCurveChartData(inflationCurve, inflationSurface, ldiCharacteristics);
    const volatilitySensitivityChartData = getVolatilitySensitivityChartData(infAdj, vol, inflationSurface, ldiCharacteristics, surfaces, views[currentView].map(x => x.label));
    const priceChartData = getPriceChartData(inflationSurface, ldiCharacteristics, vol, infAdj, items);
    const keyFiguresTableData = getKeyFiguresTableData(inflationSurface, ldiCharacteristics, vol, infAdj, items, inflationSensitivityChartData.value, views[currentView]);
    const units = views[currentView][0].units;
    const options = Object.keys(views);
    const legend = views[currentView].map(x => x.legend);
    const yAxisFormatter = views[currentView][0].units === 'percentage' ? d3.format('.0%') :
      views[currentView][0].units === 'ratio' ? d3.format('.0%') : d3.formatPrefix(',.0', 1e6);
    const showMaximizedChart = state.local.showMaximizedChart;
    const maximizedChart = state.local.maximizedChart;
    const isLoading = false;

    return {
      isLoading,
      showMaximizedChart,
      maximizedChart,
      items,
      infAdj,
      vol,
      initialInfAdj,
      initialVol,
      threshold,
      tolerance,
      currentView,
      triggersEnabled,
      inflationCurveChartData,
      inflationSensitivityChartData,
      volatilitySensitivityChartData,
      priceChartData,
      keyFiguresTableData,
      units,
      options,
      legend,
      yAxisFormatter,
    }
  }
  catch (e) {
    console.warn(e);
    return {
      isLoading: false,
      isError: true,
      errorInfo: e,
    }
  }
}

const mapDispatchToProps = ({
  onSelected: (key, value) => select(key, value, 'investible_benchmark'),
  onToggleTriggers: toggleTriggers,
  onMaximized: chart => showMaximizedChart(chart),
  onMinimized: hideMaximizedChart,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dashboard);



// WEBPACK FOOTER //
// ./src/dashboards/InvestibleBenchmark/Dashboard.js